/**
 * CategoryIcon Component
 * Renders category icons with support for abbreviation text or custom SVG
 */

import React from 'react';
import { cn } from '@/lib/utils';
import DOMPurify from 'dompurify';
import { unescapeSvg } from '@/lib/svgNormalizer';

export interface CategoryIconConfig {
  icon_type: 'abbreviation' | 'svg';
  abbreviation?: string;
  icon_shape?: 'square' | 'circle';
  icon_width?: number;
  icon_height?: number;
  fill_color?: string;
  border_color?: string;
  text_color?: string;
  icon_svg?: string | null;
  category?: string;
}

interface CategoryIconProps {
  category: CategoryIconConfig;
  size?: number;
  className?: string;
  onClick?: () => void;
}

/**
 * Generate abbreviation from category name
 */
function generateAbbreviation(name: string): string {
  const words = name
    .toUpperCase()
    .trim()
    .split(/[\s_-]+/);
  if (words.length === 1) {
    return words[0].substring(0, 3);
  }
  return words
    .slice(0, 4)
    .map(w => w[0])
    .join('');
}

export function CategoryIcon({ category, size = 24, className, onClick }: CategoryIconProps) {
  const {
    icon_type,
    abbreviation,
    icon_shape = 'square',
    icon_width,
    icon_height,
    fill_color = '#6B7280',
    border_color = '#374151',
    text_color = '#FFFFFF',
    icon_svg,
    category: categoryName,
  } = category;

  // Use provided dimensions or fall back to size
  const width = icon_width || size;
  const height = icon_height || size;

  // If custom SVG is provided (normalized at upload time)
  // Just unescape, sanitize, and apply color via CSS
  if (icon_type === 'svg' && icon_svg) {
    const cleanedSvg = unescapeSvg(icon_svg);
    const sanitizedSVG = DOMPurify.sanitize(cleanedSvg, {
      USE_PROFILES: { svg: true, svgFilters: true },
    });

    return (
      <div
        className={cn(
          'flex items-center justify-center flex-shrink-0 overflow-hidden [&>svg]:max-w-full [&>svg]:max-h-full [&>svg]:w-auto [&>svg]:h-auto',
          className,
        )}
        style={{ width, height, color: fill_color || '#6B7280' }}
        dangerouslySetInnerHTML={{ __html: sanitizedSVG }}
        onClick={onClick}
      />
    );
  }

  // Default: Abbreviation with shape (square/rectangle or circle)
  const abbrev = abbreviation || (categoryName ? generateAbbreviation(categoryName) : '?');
  const fontSize = Math.min(width, height) * 0.35; // ~35% of smaller dimension

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('flex-shrink-0', className)}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : undefined }}
    >
      {/* Shape background */}
      {icon_shape === 'circle' ? (
        <ellipse
          cx={width / 2}
          cy={height / 2}
          rx={width / 2 - 0.5}
          ry={height / 2 - 0.5}
          fill={fill_color}
          stroke={border_color}
          strokeWidth={1}
        />
      ) : (
        // Square/Rectangle
        <rect
          x={0.5}
          y={0.5}
          width={width - 1}
          height={height - 1}
          fill={fill_color}
          stroke={border_color}
          strokeWidth={1}
          rx={2} // Slight rounding for aesthetics
        />
      )}

      {/* Abbreviation text */}
      <text
        x={width / 2}
        y={height / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill={text_color}
        fontSize={fontSize}
        fontWeight="600"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        {abbrev}
      </text>
    </svg>
  );
}

/**
 * SVG Validation result interface
 */
export interface SVGValidationResult {
  valid: boolean;
  error?: string;
  warnings: string[];
  dimensions?: { width: number; height: number };
  resizedSvg?: string; // If SVG was auto-resized, this contains the resized version
}

/**
 * Validate uploaded SVG for use as category icon
 * Auto-resizes large SVGs to fit within 64x64 while maintaining aspect ratio
 */
export function validateSVG(svgString: string): SVGValidationResult {
  const warnings: string[] = [];

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');

    // Check for parsing errors
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      return { valid: false, error: 'Invalid SVG format', warnings };
    }

    const svg = doc.querySelector('svg');
    if (!svg) {
      return { valid: false, error: 'No SVG element found', warnings };
    }

    // Check dimensions
    const viewBox = svg.getAttribute('viewBox');
    const widthAttr = svg.getAttribute('width');
    const heightAttr = svg.getAttribute('height');

    let svgWidth = 0;
    let svgHeight = 0;

    if (viewBox) {
      const parts = viewBox.split(/\s+/);
      if (parts.length >= 4) {
        svgWidth = parseFloat(parts[2]);
        svgHeight = parseFloat(parts[3]);
      }
    } else if (widthAttr && heightAttr) {
      svgWidth = parseFloat(widthAttr);
      svgHeight = parseFloat(heightAttr);
    }

    // Auto-resize large SVGs instead of rejecting them
    let resizedSvg: string | undefined;
    if (svgWidth > 64 || svgHeight > 64) {
      // Calculate scale factor to fit within 64x64 while maintaining aspect ratio
      const scale = Math.min(64 / svgWidth, 64 / svgHeight);
      const newWidth = Math.round(svgWidth * scale);
      const newHeight = Math.round(svgHeight * scale);

      // Ensure viewBox is preserved for proper scaling
      if (!viewBox) {
        svg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
      }

      // Set new dimensions
      svg.setAttribute('width', String(newWidth));
      svg.setAttribute('height', String(newHeight));

      resizedSvg = new XMLSerializer().serializeToString(svg);
      warnings.push(
        `SVG was automatically resized from ${Math.round(svgWidth)}x${Math.round(svgHeight)} to ${newWidth}x${newHeight}`,
      );
    }

    if (svgWidth === 0 || svgHeight === 0) {
      warnings.push('SVG has no explicit dimensions. Consider adding width/height or viewBox.');
    }

    // Check for complex elements that may not render well
    const complexElements = svg.querySelectorAll('filter, mask, clipPath, use, symbol, pattern');
    if (complexElements.length > 0) {
      warnings.push(
        'SVG contains complex elements (filters, masks, patterns) that may not render well at small sizes.',
      );
    }

    // Check for external references
    const externalRefs = svg.querySelectorAll('[href^="http"], [xlink\\:href^="http"]');
    if (externalRefs.length > 0) {
      return {
        valid: false,
        error: 'SVG contains external references which are not allowed for security reasons.',
        warnings,
      };
    }

    // Check path complexity
    const paths = svg.querySelectorAll('path');
    paths.forEach((path, i) => {
      const d = path.getAttribute('d') || '';
      if (d.length > 500) {
        warnings.push(
          `Path ${i + 1} is very complex (${d.length} chars) and may not render well at small sizes.`,
        );
      }
    });

    // Check for scripts (security)
    const scripts = svg.querySelectorAll('script');
    if (scripts.length > 0) {
      return {
        valid: false,
        error: 'SVG contains scripts which are not allowed for security reasons.',
        warnings,
      };
    }

    // Check for event handlers (security)
    const allElements = svg.querySelectorAll('*');
    for (const el of allElements) {
      const attrs = el.getAttributeNames();
      for (const attr of attrs) {
        if (attr.startsWith('on')) {
          return {
            valid: false,
            error: `SVG contains event handler (${attr}) which is not allowed for security reasons.`,
            warnings,
          };
        }
      }
    }

    return {
      valid: true,
      warnings,
      dimensions: svgWidth > 0 ? { width: svgWidth, height: svgHeight } : undefined,
      resizedSvg,
    };
  } catch (e) {
    return { valid: false, error: 'Failed to parse SVG', warnings };
  }
}

/**
 * Resize SVG to target dimensions while maintaining aspect ratio
 */
export function resizeSVGForIcon(svgString: string, targetSize: number = 24): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svg = doc.querySelector('svg');

    if (!svg) return svgString;

    // Set new dimensions
    svg.setAttribute('width', String(targetSize));
    svg.setAttribute('height', String(targetSize));

    // Ensure viewBox is set for proper scaling
    if (!svg.getAttribute('viewBox')) {
      const origWidth = parseFloat(svg.getAttribute('width') || '24');
      const origHeight = parseFloat(svg.getAttribute('height') || '24');
      svg.setAttribute('viewBox', `0 0 ${origWidth} ${origHeight}`);
    }

    return new XMLSerializer().serializeToString(svg);
  } catch {
    return svgString;
  }
}
